import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, TrendingUp, Heart, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { blogPostsData, profileData } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const featuredPosts = blogPostsData.filter(post => post.featured);
  const regularPosts = blogPostsData.filter(post => !post.featured);

  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState(null); // 'loading' | 'success' | 'already' | 'error'
  const [subMessage, setSubMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubStatus('loading');
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() });
      if (data.status === 'already_subscribed') {
        setSubStatus('already');
        setSubMessage(data.message || "You're already subscribed.");
      } else {
        setSubStatus('success');
        setSubMessage(data.message || 'Subscribed! Check your inbox.');
        setEmail('');
      }
      setTimeout(() => { setSubStatus(null); setSubMessage(''); }, 6000);
    } catch (err) {
      console.error('Newsletter subscribe error:', err);
      setSubStatus('error');
      setSubMessage(err?.response?.data?.detail || 'Subscription failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#faf6ee]" data-testid="blog-page">
      <Header />

      <section className="pt-32 pb-16 bg-[#faf6ee]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 bg-gold-400/10 text-gold-400 text-sm font-semibold rounded-full mb-4">Insights & Perspectives</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#152846] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Blog & <span className="text-gold-400">Thought Leadership</span></h1>
            <p className="text-lg text-[#5c6878] leading-relaxed">Sharing insights from over two decades of experience in HR, leadership, and digital transformation across the IT consulting industry.</p>
          </div>
        </div>
      </section>

      {featuredPosts.length > 0 && (
        <section className="py-12 bg-[#f3ede0]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-8">
              <TrendingUp className="w-5 h-5 text-gold-400" strokeWidth={2.5} />
              <h2 className="text-xl font-bold text-[#152846]" style={{ fontFamily: "'Playfair Display', serif" }}>Most Popular Articles</h2>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              {featuredPosts.map((post, index) => (
                <article key={post.id} className={`group bg-[#fffdf8] rounded-2xl overflow-hidden border border-[#e6dfd0] hover:border-gold-400/30 hover:shadow-xl transition-all duration-300 ${index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`} data-testid={`featured-post-${post.id}`}>
                  <Link to={`/blog/${post.id}`} className="block h-full">
                    <div className={`relative overflow-hidden ${index === 0 ? 'h-64 lg:h-80' : 'h-40'}`}>
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute top-4 left-4 flex items-center gap-2">
                        <span className="px-3 py-1 bg-gold-400 text-black text-xs font-semibold rounded-full">{post.category}</span>
                        <span className="px-3 py-1 bg-gold-300 text-black text-xs font-semibold rounded-full flex items-center gap-1"><TrendingUp size={12} strokeWidth={2.5} />Featured</span>
                      </div>
                      {post.engagement && <div className="absolute bottom-4 left-4 flex items-center gap-1 text-[#152846] text-sm"><Heart size={14} className="fill-red-500 text-red-500" strokeWidth={2.5} /><span>{post.engagement}</span></div>}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-4 text-sm text-[#8b94a3] mb-3">
                        <span className="flex items-center gap-1"><Calendar size={14} strokeWidth={2.5} />{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="flex items-center gap-1"><Clock size={14} strokeWidth={2.5} />{post.readTime}</span>
                      </div>
                      <h3 className={`font-bold text-[#152846] mb-2 group-hover:text-gold-400 transition-colors line-clamp-2 ${index === 0 ? 'text-xl lg:text-2xl' : 'text-lg'}`} style={{ fontFamily: "'Playfair Display', serif" }}>{post.title}</h3>
                      <p className={`text-[#5c6878] line-clamp-2 ${index === 0 ? 'mb-4' : 'text-sm'}`}>{post.excerpt}</p>
                      {index === 0 && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#e6dfd0]">
                          <div className="flex items-center gap-2">
                            <img src={profileData.photo} alt={post.author} className="w-8 h-8 rounded-full object-cover" />
                            <span className="text-sm font-medium text-[#3e4858]">{post.author}</span>
                          </div>
                          <span className="flex items-center gap-1 text-gold-400 font-medium text-sm group-hover:gap-2 transition-all">Read More<ArrowRight size={16} strokeWidth={2.5} /></span>
                        </div>
                      )}
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-xl font-bold text-[#152846] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>All Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularPosts.map((post) => (
              <article key={post.id} className="group bg-[#fffdf8] rounded-2xl overflow-hidden border border-[#e6dfd0] hover:border-gold-400/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-testid={`blog-post-${post.id}`}>
                <Link to={`/blog/${post.id}`} className="block">
                  <div className="relative overflow-hidden h-44">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-gold-400 text-black text-xs font-semibold rounded-full">{post.category}</span>
                    {post.engagement && <div className="absolute bottom-4 left-4 flex items-center gap-1 text-[#152846] text-sm"><Heart size={14} className="fill-red-500 text-red-500" strokeWidth={2.5} /><span>{post.engagement}</span></div>}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-4 text-sm text-[#8b94a3] mb-3">
                      <span className="flex items-center gap-1"><Calendar size={14} strokeWidth={2.5} />{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      <span className="flex items-center gap-1"><Clock size={14} strokeWidth={2.5} />{post.readTime}</span>
                    </div>
                    <h3 className="font-bold text-[#152846] mb-2 group-hover:text-gold-400 transition-colors line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>{post.title}</h3>
                    <p className="text-[#5c6878] text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                    <span className="flex items-center gap-1 text-gold-400 font-medium text-sm group-hover:gap-2 transition-all">Read More<ArrowRight size={16} strokeWidth={2.5} /></span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gold-400">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Stay Updated with Latest Insights</h2>
          <p className="text-black/70 mb-8">Subscribe to receive notifications when new articles are published.</p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={subStatus === 'loading'}
              placeholder="Enter your email"
              className="px-5 py-3 rounded-full bg-black/10 border border-black/20 text-black placeholder-black/50 focus:outline-none focus:ring-2 focus:ring-black/30 w-full sm:w-80 disabled:opacity-60"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={subStatus === 'loading'}
              className="px-6 py-3 bg-black text-gold-400 font-semibold rounded-full hover:bg-gray-900 transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
              data-testid="newsletter-subscribe-btn"
            >
              {subStatus === 'loading' ? (<><Loader2 size={16} className="animate-spin" strokeWidth={2.5} />Subscribing…</>) : 'Subscribe'}
            </button>
          </form>
          {subStatus === 'success' && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-black/10 border border-black/20 rounded-full text-black font-medium" data-testid="newsletter-success">
              <CheckCircle size={18} strokeWidth={2.5} />{subMessage}
            </div>
          )}
          {subStatus === 'already' && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-black/10 border border-black/20 rounded-full text-black font-medium" data-testid="newsletter-already">
              <CheckCircle size={18} strokeWidth={2.5} />{subMessage}
            </div>
          )}
          {subStatus === 'error' && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-red-900/20 border border-red-900/40 rounded-full text-red-900 font-medium" data-testid="newsletter-error">
              <AlertCircle size={18} strokeWidth={2.5} />{subMessage}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
